import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';
import { UserGidService } from '../user-gid/user-gid.service';
import { SessionId } from '../session/session.type';
import { OdataAppVersionPayload } from './odata-appversion-payload.interface';
import { UserMasterService } from '../user-master/user-master.service';
import { AppVersion } from '../../interfaces/app-version.interface';
import { EnvironmentService } from '@evorait/pace-environment';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';

@Injectable({
    providedIn: 'root',
})
export class AppVersionAndSessionService {
    private latestBuildVersionInfo = new BehaviorSubject<AppVersion | null>(null);
    public latestBuildVersionInfo$ = this.latestBuildVersionInfo.asObservable();

    public constructor(
        private readonly http: HttpClient,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly userGidService: UserGidService,
        private readonly uriUtilities: UriUtilitiesService,
        private readonly userMasterService: UserMasterService,
        private readonly environmentService: EnvironmentService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    public sendAppVersionAndSessionId(sessionId: SessionId): Observable<void> {
        const payload: OdataAppVersionPayload = {
            app_version: this.clientManager.getAppVersion(),
            app_name: this.userMasterService.getSharpServiceText(),
            front_id: this.userGidService.getUserGid(),
            session_id: sessionId,
        };
        return this.http
            .post(this.uriUtilities.getUriForContent() + ENDPOINTS.AppVersionSet, payload)
            .pipe(map(() => undefined));
    }

    public updateLatestBuildVersionInfo(): Observable<AppVersion | null> {
        const hostUrl = this.environmentService.apiBase;
        const appVersionEndpoint = ENDPOINTS.BuildVersion;
        return this.http.get<AppVersion>(`${hostUrl}${appVersionEndpoint}`).pipe(
            tap((version) => {
                this.latestBuildVersionInfo.next(version);
            }),
            catchError((err) => {
                this.log.error('failed to get latest app version info', err);
                return of(null);
            }),
        );
    }
}
